import { useCallback, useEffect, useState } from "react";
import { MailCheck, MailOpen, MailX, Mail, Clock, RefreshCw, MousePointerClick, AlertTriangle, Timer, PauseCircle } from "lucide-react";
import { fmtDateTime, relTime } from "./crm";

const STATUS = {
  sent: { label: "Sent", icon: Mail, cls: "border-slate-200 bg-slate-50 text-slate-600", hint: "Handed to the mail provider" },
  queued: { label: "Queued", icon: Clock, cls: "border-slate-200 bg-slate-50 text-slate-600", hint: "Waiting to be sent" },
  scheduled: { label: "Scheduled", icon: Clock, cls: "border-slate-200 bg-slate-50 text-slate-600", hint: "Scheduled for later" },
  delivered: { label: "Delivered", icon: MailCheck, cls: "border-emerald-200 bg-emerald-50 text-emerald-700", hint: "Accepted by the recipient's inbox" },
  delivery_delayed: { label: "Delayed", icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-700", hint: "Inbox is slow to accept it; retrying" },
  opened: { label: "Opened", icon: MailOpen, cls: "border-sky-200 bg-sky-50 text-sky-700", hint: "The recruit opened this e-mail" },
  clicked: { label: "Clicked", icon: MousePointerClick, cls: "border-sky-200 bg-sky-50 text-sky-700", hint: "The recruit clicked a link" },
  bounced: { label: "Bounced", icon: MailX, cls: "border-rose-200 bg-rose-50 text-rose-700", hint: "Address rejected the message — check the e-mail" },
  complained: { label: "Spam report", icon: AlertTriangle, cls: "border-rose-200 bg-rose-50 text-rose-700", hint: "Recipient marked it as spam" },
  failed: { label: "Failed", icon: MailX, cls: "border-rose-200 bg-rose-50 text-rose-700", hint: "Could not be sent" },
  canceled: { label: "Canceled", icon: MailX, cls: "border-slate-200 bg-slate-50 text-slate-500", hint: "Send was canceled" },
};

export function EmailStatusBadge({ status }) {
  const m = STATUS[status] || STATUS.sent;
  const Icon = m.icon;
  return (
    <span title={m.hint} data-testid={`email-status-${status}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function DripStatus({ drip }) {
  const done = drip.steps_sent >= drip.steps_total;
  return (
    <div className={`mb-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] ${drip.active ? "border-cyan-200 bg-cyan-50 text-cyan-800" : "border-slate-200 bg-slate-50 text-slate-600"}`} data-testid="lead-drip-status">
      {drip.active ? <Timer className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <PauseCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
      <div>
        <span className="font-semibold">Follow-up drip · {drip.steps_sent} of {drip.steps_total} sent</span>
        {drip.active && <span> · next e-mail ({drip.next_step} of {drip.steps_total}) goes out {new Date(drip.next_at) > Date.now() ? `on ${fmtDateTime(drip.next_at)}` : "within the hour"}</span>}
        {!drip.active && <span> · {done ? "sequence complete" : drip.paused_reason}</span>}
      </div>
    </div>
  );
}

export default function EmailLog({ leadId, onLoad }) {
  const [emails, setEmails] = useState(null);
  const [drip, setDrip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setBusy(true); setError("");
    try { const data = await onLoad(leadId); setEmails(data.emails); setDrip(data.drip); } catch { setError("Could not load e-mail history."); } finally { setBusy(false); }
  }, [leadId, onLoad]);

  useEffect(() => { setEmails(null); refresh(); }, [refresh]);

  return (
    <div data-testid="lead-emails">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[11px] text-slate-500">E-mails sent to this recruit. Status updates each time you open this tab.</p>
        <button onClick={refresh} disabled={busy} data-testid="lead-emails-refresh" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-slate-300 disabled:opacity-50">
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {error && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="lead-emails-error">{error}</p>}
      {drip && <DripStatus drip={drip} />}
      {emails === null && !error && <p className="py-4 text-center text-xs text-slate-400">Loading…</p>}
      {emails?.length === 0 && <p className="py-4 text-center text-xs text-slate-400" data-testid="lead-emails-empty">No e-mails have been sent to this recruit yet.</p>}
      <ul className="space-y-2">
        {emails?.map((m) => (
          <li key={m.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`lead-email-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{m.subject}</p>
                <p className="mt-0.5 truncate text-[11px] text-slate-500">to {m.to} · sent {fmtDateTime(m.created_at)}</p>
                {m.reply_to && <p className="mt-0.5 truncate text-[11px] text-slate-500" data-testid={`lead-email-replyto-${m.id}`}>replies go to {m.reply_to}</p>}
              </div>
              <EmailStatusBadge status={m.status} />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {m.status !== "sent" && m.status !== "failed" ? `${STATUS[m.status]?.label || m.status} ${relTime(m.status_at)} · ` : ""}
              {m.provider === "resend" ? "Tracked via Resend" : "Sent via managed mailer (no delivery tracking)"}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
